import React, { useContext, useEffect, useRef, useState } from 'react';

import { Box, Button, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CircularProgress from '@mui/material/CircularProgress';

import { DataContext } from '../data/DataContext';
import { DataContextType } from '../data_types/interfaces';
import { DS } from './Dashboard';

/*****************************************************************************
 ** Player ID parsing, matching FireBaseEvents.ParsePlayerIdList in the game:
 ** comma, space or newline separated, trimmed and de-duplicated.
*****************************************************************************/

// Firebase keys can't contain these, so an ID with them can't be a player
const INVALID_KEY_CHARS = /[.#$[\]/]/;

const splitPlayerIds = (ids: string[]) => {
    const unique = Array.from(new Set(ids.map((id) => id.trim()).filter(Boolean)));
    return {
        valid: unique.filter((id) => !INVALID_KEY_CHARS.test(id)),
        invalid: unique.filter((id) => INVALID_KEY_CHARS.test(id)),
    };
};

export const parsePlayerIds = (raw: string) => splitPlayerIds(raw.split(/[\s,]+/));

/*****************************************************************************
 ** Importing IDs from a file. Text files use the same separators as the input
 ** field. JSON can be an array of IDs, an array of objects with a playerId or
 ** id field, or an object keyed by ID (the shape of events/inviteAccess/{id}).
*****************************************************************************/

const MAX_IMPORT_BYTES = 1024 * 1024;

const playerIdsFromJson = (data: unknown): string[] => {
    if (Array.isArray(data)) {
        return data.map((item) => {
            if (typeof item === 'string' || typeof item === 'number') return String(item);
            if (item && typeof item === 'object') {
                const { playerId, id } = item as { playerId?: unknown; id?: unknown };
                if (typeof playerId === 'string') return playerId;
                if (typeof id === 'string') return id;
            }
            throw new Error('Every entry in the array needs to be an ID, or an object with a "playerId" or "id" field.');
        });
    }

    if (data && typeof data === 'object') {
        return Object.keys(data);
    }

    throw new Error('Expected an array of player IDs or an object keyed by player ID.');
};

export const readPlayerIdFile = async (file: File) => {
    if (file.size > MAX_IMPORT_BYTES) {
        throw new Error('The file is larger than 1 MB.');
    }

    const text = await file.text();
    if (!file.name.toLowerCase().endsWith('.json')) {
        return parsePlayerIds(text);
    }

    let data: unknown;
    try {
        data = JSON.parse(text);
    } catch {
        throw new Error('The file is not valid JSON.');
    }
    return splitPlayerIds(playerIdsFromJson(data));
};

/*****************************************************************************
 ** Name lookup, cached for the session so lists don't refetch on every render
*****************************************************************************/

const nameCache = new Map<string, Promise<string | null>>();

const usePlayerName = (playerId: string) => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { lookupPlayerName, currentSeason } = context || {};
    const [name, setName] = useState<string | null | undefined>(undefined);

    useEffect(() => {
        if (!lookupPlayerName || !currentSeason) return;

        let cancelled = false;
        const cacheKey = `${currentSeason}/${playerId}`;
        if (!nameCache.has(cacheKey)) {
            nameCache.set(cacheKey, lookupPlayerName(playerId));
        }
        nameCache.get(cacheKey)!.then((result) => {
            if (!cancelled) setName(result);
        });

        return () => {
            cancelled = true;
        };
    }, [playerId, lookupPlayerName, currentSeason]);

    return name;
};

/*****************************************************************************
 ** Entry field: paste one or many IDs and press Enter or Add, or import a
 ** .txt / .json file (button or drag and drop). Imports only ever add players.
*****************************************************************************/

interface InvitePlayerInputProps {
    onAdd: (playerIds: string[]) => void | Promise<void>;
    existing: string[];
    busy?: boolean;
    buttonLabel?: string;
}

interface PendingImport {
    fileName: string;
    fresh: string[];
    alreadyAdded: number;
    invalid: string[];
}

const importButtonSx = { flexShrink: 0, height: 40, borderRadius: '10px', textTransform: 'none', fontWeight: 700 };

export const InvitePlayerInput: React.FC<InvitePlayerInputProps> = ({ onAdd, existing, busy, buttonLabel = 'Add' }) => {
    const [value, setValue] = useState('');
    const [message, setMessage] = useState<{ text: string; isError: boolean } | null>(null);
    const [pendingImport, setPendingImport] = useState<PendingImport | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleAdd = async () => {
        const { valid, invalid } = parsePlayerIds(value);
        const fresh = valid.filter((id) => !existing.includes(id));
        const alreadyAdded = valid.length - fresh.length;

        if (invalid.length > 0) {
            // Keep the bad IDs in the field so they can be fixed
            setValue(invalid.join(', '));
            setMessage({ text: `Not a valid player ID: ${invalid.join(', ')}`, isError: true });
        } else {
            setValue('');
            setMessage(alreadyAdded > 0 ? { text: `${alreadyAdded} already on the list`, isError: false } : null);
        }

        if (fresh.length > 0) {
            await onAdd(fresh);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleAdd();
        }
    };

    const handleFile = async (file: File) => {
        setPendingImport(null);
        setMessage(null);

        try {
            const { valid, invalid } = await readPlayerIdFile(file);
            const fresh = valid.filter((id) => !existing.includes(id));

            if (valid.length === 0 && invalid.length === 0) {
                setMessage({ text: `No player IDs found in ${file.name}`, isError: true });
                return;
            }

            setPendingImport({ fileName: file.name, fresh, alreadyAdded: valid.length - fresh.length, invalid });
        } catch (error) {
            setMessage({ text: `Could not read ${file.name}: ${(error as Error).message}`, isError: true });
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // Clear it so picking the same file again still fires a change
        e.target.value = '';
        if (file) handleFile(file);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file && !busy) handleFile(file);
    };

    const confirmImport = async () => {
        if (!pendingImport) return;
        const { fresh } = pendingImport;
        setPendingImport(null);
        await onAdd(fresh);
    };

    return (
        <Box
            onDragOver={(e) => {
                e.preventDefault();
                if (!busy) setIsDragging(true);
            }}
            onDragLeave={(e) => {
                // Ignore leaving into a child element
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false);
            }}
            onDrop={handleDrop}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                m: -1,
                p: 1,
                borderRadius: '12px',
                border: `1px dashed ${isDragging ? DS.violet : 'transparent'}`,
                background: isDragging ? alpha(DS.violet, 0.08) : 'transparent',
                transition: 'border-color 0.15s ease, background 0.15s ease',
            }}
        >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <TextField
                    multiline
                    maxRows={4}
                    size="small"
                    label="Player IDs"
                    placeholder="Paste one or more IDs, separated by commas, spaces or new lines"
                    value={value}
                    onChange={(e) => {
                        setValue(e.target.value);
                        setMessage(null);
                    }}
                    onKeyDown={handleKeyDown}
                    disabled={busy}
                    error={!!message?.isError}
                    helperText={message?.text || (isDragging ? 'Drop the file to import it' : 'Press Enter to add, or drop a .txt or .json file here')}
                    InputProps={{ sx: { fontFamily: 'monospace', fontSize: '13px' } }}
                    sx={{ flex: '1 1 260px', minWidth: 0 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={handleAdd}
                        disabled={busy || value.trim() === ''}
                        startIcon={busy ? <CircularProgress size={16} /> : <PersonAddIcon />}
                        sx={importButtonSx}
                    >
                        {buttonLabel}
                    </Button>
                    <Tooltip title="Import IDs from a .txt or .json file">
                        <span>
                            <Button
                                variant="outlined"
                                color="inherit"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={busy}
                                startIcon={<UploadFileIcon />}
                                sx={{ ...importButtonSx, color: DS.textMuted, borderColor: DS.borderStrong }}
                            >
                                Import file
                            </Button>
                        </span>
                    </Tooltip>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".txt,.json,text/plain,application/json"
                        hidden
                        onChange={handleFileChange}
                    />
                </Box>
            </Box>

            {pendingImport && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    p: 1.5,
                    borderRadius: '10px',
                    border: `1px solid ${alpha(DS.violet, 0.35)}`,
                    background: alpha(DS.violet, 0.08),
                }}>
                    <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: '13px', fontWeight: 700, color: DS.text }}>
                            {pendingImport.fileName}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>
                            {pendingImport.fresh.length} new
                            {pendingImport.alreadyAdded > 0 && ` · ${pendingImport.alreadyAdded} already on the list`}
                            {pendingImport.invalid.length > 0 && ` · ${pendingImport.invalid.length} invalid, skipped`}
                        </Typography>
                        {pendingImport.invalid.length > 0 && (
                            <Typography noWrap title={pendingImport.invalid.join(', ')} sx={{ fontSize: '11px', color: DS.loss, fontFamily: 'monospace' }}>
                                {pendingImport.invalid.join(', ')}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            size="small"
                            onClick={() => setPendingImport(null)}
                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, color: DS.textMuted }}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={confirmImport}
                            disabled={busy || pendingImport.fresh.length === 0}
                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
                        >
                            {pendingImport.fresh.length === 0 ? 'Nothing new' : `${buttonLabel} ${pendingImport.fresh.length}`}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

/*****************************************************************************
 ** Invited player list
*****************************************************************************/

const InvitedPlayerRow: React.FC<{ playerId: string; onRemove?: (playerId: string) => void; removing: boolean; removeLabel: string }> = ({ playerId, onRemove, removing, removeLabel }) => {
    const name = usePlayerName(playerId);

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            minWidth: 0,
            px: 1.25,
            py: 0.75,
            borderRadius: '10px',
            border: `1px solid ${DS.border}`,
            background: DS.surface,
        }}>
            <Box sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                background: alpha(DS.violet, 0.16),
                color: DS.violet,
                fontSize: '12px',
                fontWeight: 800,
            }}>
                {(name || '?').charAt(0).toUpperCase()}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: '13px', fontWeight: 600, color: name ? DS.text : DS.textMuted }}>
                    {name === undefined ? 'Looking up…' : name || 'Not in the current season'}
                </Typography>
                <Typography noWrap sx={{ fontSize: '11px', color: DS.textFaint, fontFamily: 'monospace' }}>
                    {playerId}
                </Typography>
            </Box>
            {onRemove && (
                <Tooltip title={removeLabel}>
                    <span>
                        <IconButton
                            size="small"
                            aria-label={`${removeLabel} ${name || playerId}`}
                            onClick={() => onRemove(playerId)}
                            disabled={removing}
                            sx={{ color: DS.textMuted, '&:hover': { color: DS.loss } }}
                        >
                            {removing ? <CircularProgress size={14} /> : <CloseIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </Box>
    );
};

interface InvitedPlayersListProps {
    playerIds: string[];
    onRemove?: (playerId: string) => void;
    removingId?: string | null;
    removeLabel?: string;
    emptyText: string;
}

// Each row looks up a name, so long imported lists start collapsed
const COLLAPSED_LIST_SIZE = 20;

export const InvitedPlayersList: React.FC<InvitedPlayersListProps> = ({ playerIds, onRemove, removingId, removeLabel = 'Remove', emptyText }) => {
    const [showAll, setShowAll] = useState(false);

    if (playerIds.length === 0) {
        return (
            <Box sx={{
                py: 2,
                textAlign: 'center',
                borderRadius: '10px',
                border: `1px dashed ${DS.borderStrong}`,
                color: DS.textMuted,
                fontSize: '13px',
            }}>
                {emptyText}
            </Box>
        );
    }

    const hiddenCount = showAll ? 0 : Math.max(0, playerIds.length - COLLAPSED_LIST_SIZE);
    const visibleIds = hiddenCount > 0 ? playerIds.slice(0, COLLAPSED_LIST_SIZE) : playerIds;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
                {visibleIds.map((playerId) => (
                    <InvitedPlayerRow
                        key={playerId}
                        playerId={playerId}
                        onRemove={onRemove}
                        removing={removingId === playerId}
                        removeLabel={removeLabel}
                    />
                ))}
            </Box>
            {(hiddenCount > 0 || (showAll && playerIds.length > COLLAPSED_LIST_SIZE)) && (
                <Button
                    size="small"
                    onClick={() => setShowAll((prev) => !prev)}
                    sx={{ alignSelf: 'center', borderRadius: '8px', textTransform: 'none', fontWeight: 600, color: DS.textMuted }}
                >
                    {hiddenCount > 0 ? `Show all ${playerIds.length}` : 'Show fewer'}
                </Button>
            )}
        </Box>
    );
};
